import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import "./PWAInstallButton.css";

const isRunningStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const getDeviceType = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIPadDesktopMode =
    userAgent.includes("macintosh") && window.navigator.maxTouchPoints > 1;

  if (/iphone|ipad|ipod/.test(userAgent) || isIPadDesktopMode) return "ios";
  if (/android/.test(userAgent)) return "android";
  return "desktop";
};

const InstallIcon = () => (
  <svg
    aria-hidden="true"
    className="pwa-install-icon"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
    <path d="M5 16v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
  </svg>
);

function PWAInstallButton() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isRunningStandalone);
  const [showInstructions, setShowInstructions] = useState(false);
  const [installDeclined, setInstallDeclined] = useState(false);

  useEffect(() => {
    const displayMode = window.matchMedia?.("(display-mode: standalone)");

    const handleInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setInstallDeclined(false);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setShowInstructions(false);
      setInstallDeclined(false);
    };

    const handleDisplayModeChange = (event) => {
      setIsInstalled(event.matches || window.navigator.standalone === true);
    };

    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    displayMode?.addEventListener?.("change", handleDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      displayMode?.removeEventListener?.("change", handleDisplayModeChange);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) {
      setInstallDeclined(false);
      setShowInstructions(true);
      return;
    }

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      setInstallPrompt(null);
      setInstallDeclined(outcome !== "accepted");
    } catch {
      setInstallPrompt(null);
      setShowInstructions(true);
    }
  };

  if (isInstalled) return null;

  const deviceType = getDeviceType();

  return (
    <>
      <button
        className="pwa-install-button"
        type="button"
        onClick={handleInstall}
        aria-haspopup={!installPrompt ? "dialog" : undefined}
        aria-label="Install Q&A App on this device"
      >
        <InstallIcon />
        <span>Install app</span>
      </button>

      {installDeclined && (
        <div className="pwa-install-status" role="status">
          Installation cancelled. You can try again anytime.
        </div>
      )}

      {showInstructions && (
        <Modal
          title="Install Q&A App"
          onClose={() => setShowInstructions(false)}
        >
          <div className="pwa-install-instructions">
            <p>
              Add Q&amp;A to your device for quick access from your home screen
              and an app-like experience.
            </p>

            {deviceType === "ios" && (
              <ol>
                <li>
                  Open this page in <strong>Safari</strong>.
                </li>
                <li>
                  Tap the <strong>Share</strong> button in the browser toolbar.
                </li>
                <li>
                  Choose <strong>Add to Home Screen</strong>, then tap
                  <strong> Add</strong>.
                </li>
              </ol>
            )}

            {deviceType === "android" && (
              <ol>
                <li>Open your browser menu.</li>
                <li>
                  Choose <strong>Install app</strong> or
                  <strong> Add to Home screen</strong>.
                </li>
                <li>Confirm the installation.</li>
              </ol>
            )}

            {deviceType === "desktop" && (
              <ol>
                <li>
                  In Chrome or Edge, select the install icon in the address bar,
                  or open the browser menu and choose
                  <strong> Install Q&amp;A App</strong>.
                </li>
                <li>
                  In Safari, choose <strong>File</strong>, then
                  <strong> Add to Dock</strong>.
                </li>
                <li>Confirm the installation.</li>
              </ol>
            )}

            <p className="pwa-install-help">
              If the install option is missing, try opening this site in Safari,
              Chrome, or Edge.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}

export default PWAInstallButton;
